import React, { useEffect, useState } from "react";
import CustomFilters from "../components/Filters/CustomFilters";
import { FILTER_ICON } from "../components/Svgs";
import { MdKeyboardBackspace } from "react-icons/md";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { selectAuthUser } from "../redux/selectors/authSelectors";
import AdvanceSearchResult from "../components/AdvanceSearch/AdvanceSearchResults";
import { DATE_FILTER_CONDITIONS, DATE_WITH_IN } from "../constants/constants";
import { computeIntersectionSearchLayoutFields } from "../utils/filters/helpers";

const AdvanceSearchPage = () => {
    const [searchValue, setSearchValue] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [selectedRepos, setSelectedRepos] = useState([]); // Will be set to all repos in useEffect
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector(selectAuthUser);
    const contextureMetadata = useSelector((state) => state.contextureMetadata);
    const [savedSearchValue, setSavedSearchValue] = useState('new');
    const [repoOptions, setRepoOptions] = useState([]);
    const [repoFilters, setRepoFilters] = useState({});
    const [filtersConditions, setFiltersConditions] = useState({ publishDate: DATE_FILTER_CONDITIONS[0], publishDateRelDate: DATE_WITH_IN[0] });
    const [shouldSearch, setShouldSearch] = useState(false);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        if (user?.profiles?.length) {
            const optionsMap = new Map();
            user?.profiles?.forEach((repo) => {
                const id = repo?.repositoryId;
                if (!id) return;
                if (!optionsMap.has(id)) {
                    optionsMap.set(id, { label: repo?.repositoryName, value: id });
                }
            });
            const options = Array.from(optionsMap.values());
            setSelectedRepos(options); // Set all repos initially
            setRepoOptions(options);
        }
    }, [dispatch, user?.profiles])


    const selectedRepoIds = selectedRepos.map((r) => r.value);

    const intersectionSearchLayoutFields = computeIntersectionSearchLayoutFields(
        contextureMetadata?.repos,
        selectedRepoIds
    );

    const handleRepoFilterChange = (fieldName, value, reset) => {
        if (reset) {
            setRepoFilters({});
            setShouldSearch(false);
            return;
        }
        setRepoFilters((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
        setShouldSearch(false);
    };

    const handleSavedSearchChange = (value) => {
        setSavedSearchValue(value);
    };

    const handleSortChange = (field, direction) => {
        if (Array.isArray(field)) {
            // Multiple sort fields
            setSortField(field);
            setSortDirection('multiple');
        } else {
            // Single sort field
            setSortField(field);
            setSortDirection(direction);
        }
        setShouldSearch(false); // Trigger re-search when sort changes
    };


    const handleReset = () => {
        setSearchValue('');
        setSelectedRepos([]);
        setRepoFilters({});
        setSortField(null);
        setSortDirection(null);
        setShowResults(false);
    };

    const hasValidFilterValues = Object.values(repoFilters || {}).some(
        v => v !== null && v !== undefined && v !== ''
    );

    const hasValidSearchCriteria =
        selectedRepos.length > 0 &&
        (searchValue?.trim() || hasValidFilterValues);
    const handleApply = () => {
        setShouldSearch(true);
        setShowResults(true);
    };

    const handleSaveAndApply = () => {
        setShouldSearch(true);
        setShowResults(true);
    };

    const handleConditionChange = (fieldName, condition) => {
        setShouldSearch(false);
        setFiltersConditions(prev => ({
            ...prev,
            [fieldName]: condition
        }));
    }


    return (
        <>
            <div className="d-flex justify-content-start gap-2 align-items-center">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="back-btn d-flex align-items-center"
                >
                    <MdKeyboardBackspace size={16} />
                    <span>Back</span>
                </button>
                <h3 className='mytools_heading'>Advanced Search</h3>
            </div>
            <CustomFilters
                searchValue={searchValue}
                setShowResults={setShowResults}
                onSearchChange={(value) => {
                    setSearchValue(value);
                    setShouldSearch(false);
                }}
                repoOptions={repoOptions}
                selectedRepos={selectedRepos}
                onSelectedReposChange={(value) => {
                    setSelectedRepos(value);
                    setShouldSearch(false);
                }}
                repoFilters={repoFilters}
                filtersConditions={filtersConditions}
                setFiltersConditions={handleConditionChange}
                onRepoFilterChange={handleRepoFilterChange}
                onReset={handleReset}
                onApply={handleApply}
                onSaveAndApply={handleSaveAndApply}
                savedSearchValue={savedSearchValue}
                onSavedSearchChange={handleSavedSearchChange}
                searchLayoutFields={intersectionSearchLayoutFields}
                canApply={hasValidSearchCriteria}
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
            />
            <AdvanceSearchResult
                filtersConditions={filtersConditions}
                shouldSearch={shouldSearch}
                showResults={showResults}
                repoFilters={repoFilters}
                searchValue={searchValue}
                selectedRepos={selectedRepos}
                sortField={sortField}
                sortDirection={sortDirection}
            />
        </>
    );
};

export default AdvanceSearchPage;

export const AdvanceSearchPageEmptyState = () => {
    return (
        <div className="empty-state">
            <div className="empty-icon">
                <FILTER_ICON width="30" height="36" />
            </div>

            <h3>Explore Content Across Repositories</h3>

            <p>
                Results will appear once you apply filters to refine your search.<br />
                Or use a saved search to pick up where you left off.
            </p>
        </div>
    );
}